'use client';

import { usePathname } from 'next/navigation';
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
	type ReactNode,
} from 'react';

/**
 * Panel derecho del shell: la pieza donde una vista cuelga sus controles
 * secundarios — los filtros del directorio, los presets de la vista de gráficos.
 *
 * DOS MODOS, porque son dos necesidades distintas:
 *
 * `rail` — carril permanente. La vista vive con el panel al lado: ocupa su ancho
 * siempre, con una columna de iconos, y abrirlo angosta el contenido. Es para
 * vistas que trabajan CON el panel (gráficos: presets, series, escalas).
 *
 * `toggle` — no ocupa nada hasta que se lo pide. Al abrirse EMPUJA el contenido
 * en escritorio, donde sobra ancho y tapar la lista que estás filtrando sería
 * peor; en pantallas angostas se superpone a ancho completo, porque ahí no hay
 * ancho que ceder. Es para vistas donde el panel es una consulta puntual: en el
 * directorio se abren los filtros, se eligen y se cierra.
 */

/**
 * `useLayoutEffect` corre ANTES de que el navegador pinte; `useEffect`, después.
 * En el servidor no existe el layout, así que ahí se degrada a `useEffect` para
 * no disparar la advertencia de React.
 */
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export type SidePanelMode = 'rail' | 'toggle';

/**
 * Rutas que reservan el carril YA EN EL SERVIDOR.
 *
 * Sólo las de modo `rail`: son las únicas que ocupan ancho en reposo. La vista
 * registra su panel en un efecto —o sea después del primer render, y en el
 * servidor no corre ninguno—, así que sin esta lista el HTML saldría con el
 * carril en cero y al hidratar el contenido se angostaría de golpe.
 *
 * Un panel `toggle` no necesita estar acá: no ocupa ancho hasta que se abre, y
 * para entonces ya hidrató.
 */
const ROUTES_WITH_PERSISTENT_RAIL = new Set(['/graficos']);

export const SIDE_PANEL_RAIL_PORTAL_ID = 'side-panel-rail-portal';
export const SIDE_PANEL_BODY_PORTAL_ID = 'side-panel-body-portal';

interface SidePanelContextValue {
	/** Hay una vista que declaró panel. */
	active: boolean;
	mode: SidePanelMode;
	/** Panel desplegado. */
	open: boolean;
	setOpen: (open: boolean) => void;
	toggle: () => void;
	/**
	 * Si el cambio de ancho en curso debe animarse.
	 *
	 * NO se puede decidir en CSS: abrir el panel y entrar a una vista con panel
	 * terminan los dos en `data-active="true"`, y una transición se aplica según
	 * el estado DESTINO, no según qué lo provocó. Abrir DEBE animar —es una acción
	 * del usuario—; cambiar de vista NO, porque ahí ya corre la entrada de página
	 * y las dos juntas mueven el contenido en diagonal.
	 */
	animate: boolean;
	/** Título del panel abierto, provisto por la vista. */
	title: string;
	/** Registra el panel de la vista. Devuelve la baja, para el cleanup. */
	registerPanel: (title: string, mode: SidePanelMode) => () => void;
}

const SidePanelContext = createContext<SidePanelContextValue | null>(null);

export function SidePanelProvider({ children }: { children: ReactNode }) {
	const pathname = usePathname();
	const [registered, setRegistered] = useState(false);
	const [mode, setMode] = useState<SidePanelMode>('toggle');
	const [open, setOpen] = useState(false);
	const [animate, setAnimate] = useState(false);
	const [title, setTitle] = useState('');

	// Montar o desmontar un panel apaga la animación: ese cambio acompaña a una
	// navegación, y ahí el panel tiene que estar en su sitio desde el principio.
	const registerPanel = useCallback((panelTitle: string, panelMode: SidePanelMode) => {
		setAnimate(false);
		setRegistered(true);
		setMode(panelMode);
		setTitle(panelTitle);
		return () => {
			setAnimate(false);
			setRegistered(false);
			setOpen(false);
			setTitle('');
		};
	}, []);

	// La ruta basta para reservar el carril en el servidor; el registro de la vista
	// lo sostiene después.
	const routeHasRail = ROUTES_WITH_PERSISTENT_RAIL.has(pathname);
	const active = registered || routeHasRail;
	const effectiveMode: SidePanelMode = registered ? mode : routeHasRail ? 'rail' : 'toggle';

	// Las dos formas de abrir o cerrar son acciones del usuario, y ambas animan.
	const openPanel = useCallback((next: boolean) => {
		setAnimate(true);
		setOpen(next);
	}, []);

	const toggle = useCallback(() => {
		setAnimate(true);
		setOpen((v) => !v);
	}, []);

	const value = useMemo(
		() => ({
			active,
			mode: effectiveMode,
			open,
			animate,
			setOpen: openPanel,
			toggle,
			title,
			registerPanel,
		}),
		[active, effectiveMode, open, animate, openPanel, toggle, title, registerPanel],
	);

	return <SidePanelContext.Provider value={value}>{children}</SidePanelContext.Provider>;
}

export function useSidePanelShell() {
	const ctx = useContext(SidePanelContext);
	if (!ctx) {
		throw new Error('useSidePanelShell debe usarse dentro de <SidePanelProvider>');
	}
	return ctx;
}

/**
 * Lo que llama una VISTA para declarar su panel. Devuelve el estado para que la
 * vista pueda, por ejemplo, marcar su botón como activo. Fuera del provider
 * devuelve un valor inerte en vez de reventar: así una vista con panel puede
 * renderizarse en un layout sin shell (tests, páginas sueltas) sin romperse.
 */
export function useSidePanel(title: string, mode: SidePanelMode = 'toggle') {
	const ctx = useContext(SidePanelContext);
	const registerPanel = ctx?.registerPanel;

	// Antes del primer pintado, no después: así la vista aparece con el panel ya
	// en su sitio y no hay un salto de corrección a la vista del usuario.
	useIsomorphicLayoutEffect(() => {
		if (!registerPanel) return;
		return registerPanel(title, mode);
	}, [registerPanel, title, mode]);

	return {
		open: ctx?.open ?? false,
		setOpen: ctx?.setOpen ?? (() => {}),
		toggle: ctx?.toggle ?? (() => {}),
		available: ctx != null,
	};
}
